import type { ReactNode } from "react";

import { cx } from "@/lib/cx";
import { Button } from "./Button";
import { DisclosureChevron, Tick } from "./list/atoms";
import { hoverReveal } from "./tones";

/**
 * "Tick + title + meta + actions" — the row the *data* surfaces are made of.
 *
 * This is the **dense** row, not the settings row. `GroupedRow` is
 * settings-shaped content — 40px, a value, a chevron; this is the one that
 * holds 27 carried-over tasks. `UI-REVIEW.md` §6's density targets win here
 * over the old grouped row: **one 40px desktop row**. It rises to the 44px
 * floor on a coarse pointer — density is about how much fits on a monitor,
 * never about shrinking a touch target.
 *
 * It is *not* a row on the page background any more. Tasks and Inbox both
 * render it inside clipped panel surfaces, so the row is square, full-bleed,
 * and the list owner draws the shared hairline between records.
 *
 * Four decisions are baked in rather than exposed, because every one of them
 * was got wrong somewhere in the app:
 *
 * 1. **The whole row is the tick target** — unless {@link ListRowProps.onTitleClick}
 *    splits it. Workout wants the whole row: it is used one-handed, between
 *    sets. Tasks needs the split, because a task's name is edited constantly
 *    and a row that both ticks and renames from one hit area makes the common
 *    case impossible without a menu.
 * 2. **Hover-revealed actions still exist on a phone.** They rest visible and
 *    only hide where the device can actually hover (see {@link hoverReveal}).
 *    Before this, "Tomorrow", Edit and Delete were `opacity: 0` on every task
 *    row in the PWA — not hard to find, *absent* (P7).
 * 3. **One tick, and it is the vivid accent.** `role="checkbox"` +
 *    `aria-checked`, filled with `bg-accent` and marked with `text-accent-on`.
 *    It was `bg-focus` — which still resolves, and is still AA, and is the dark
 *    muted teal the accent split exists to get rid of. A tick is a mark, not
 *    text.
 * 4. **The selected row is not a tint.** A 3px `rail-selected` plus a weight
 *    bump — two independent channels, which is what P6 asks for. It is
 *    explicitly carries no resting elevation: a resting row does not float.
 *
 * Height comes from the shared row recipe: 40px with a fine pointer, 44px with
 * a coarse one. Set `density` only for source compatibility; both legacy names
 * resolve to that same floor.
 */

export type ListRowDensity = "compact" | "comfortable";

type ListRowBaseProps = {
  /** How many lines the title may occupy before it clamps. */
  titleLines?: 1 | 2;
  /** The quiet second line — plan/actual, due date, tags. Omit when empty. */
  meta?: ReactNode;

  /** Tick state. Omit `onToggle` for a row that is not tickable. */
  checked?: boolean;
  onToggle?: (next: boolean) => void;
  /**
   * The accessible name of the tick, and therefore of the row. Required with
   * `onToggle` — the row's contents are punctuation to a screen reader.
   */
  toggleLabel?: string;

  /**
   * Opens the row's detail (the side peek). When the row is tickable this
   * renders as its own always-visible trailing control, because the row body
   * is already spoken for; when it is not, the body itself opens.
   */
  onOpen?: () => void;
  /** Accessible name for that control, e.g. `Open Ship the importer`. */
  openLabel?: string;
  /**
   * Accessible name for the always-visible disclosure button when the row body
   * also opens the same detail. Keep it distinct from {@link openLabel} so two
   * controls on one row are not announced with the same name.
   */
  openAccessoryLabel?: string;

  /**
   * What clicking the row's *text* does, when that is not "tick it".
   *
   * Without this the whole row is one tick target — right for the workout list,
   * where a row is a thing you complete and its name is never edited. A task's
   * name is edited constantly, so on that list the text opens the inline editor
   * and only the box ticks. Supplying this splits the two into separate
   * controls; omitting it keeps the whole-row tick.
   */
  onTitleClick?: () => void;
  /**
   * Accessible name for the title button, e.g. `Open Call the accountant`.
   *
   * It must differ from {@link ListRowProps.openLabel} when both are set: they
   * are two separate controls on one row, and giving them one name makes a
   * screen reader announce the same button twice.
   */
  titleClickLabel?: string;

  /**
   * The leading gutter — a drag handle. The list owns dragging, not the row.
   *
   * The gutter is 20px with a mouse and widens to the 44px tap floor on a
   * coarse pointer, because the handle inside it can only be as wide as the
   * gutter: a 44×20 target is still not a target, and dragging is the one
   * interaction on this row with no keyboard-free alternative.
   */
  leading?: ReactNode;
  /** Always-visible trailing content: badges, counts, a due date. */
  trailing?: ReactNode;
  /** Trailing controls that hide only where the pointer can hover. */
  actions?: ReactNode;

  /** Open / focused in the peek. Draws the rail, not a tint. */
  selected?: boolean;
  /** Blocks the tick without hiding the row. */
  disabled?: boolean;
  /** An optimistic row with no server record yet. Dims, stays readable. */
  pending?: boolean;
  density?: ListRowDensity;

  as?: "li" | "div";
  className?: string;
};

/**
 * A row shows a title **or** an editor for one, never both — so the two are a
 * union rather than two optional props. Passing `titleEditor` stands the tick,
 * the open chevron, `trailing` and `actions` down: an `<input>` inside a
 * `<button role="checkbox">` is invalid HTML and would toggle the tick on every
 * click, and a row being renamed has no business offering Delete.
 *
 * The editor keeps the gutter and the tick's footprint, so **nothing moves
 * sideways** when the row flips into edit mode — which is the entire promise of
 * "edits in place". Commit and cancel belong to the caller; blur is usually
 * handled by wrapping the row in an element whose `onBlur` checks
 * `relatedTarget`.
 */
export type ListRowProps = ListRowBaseProps &
  (
    | {
        /** The row's main line. Clamped to one line unless `titleLines` says more. */
        title: ReactNode;
        titleEditor?: undefined;
      }
    | { title?: undefined; titleEditor: ReactNode }
  );

export function ListRow({
  title,
  titleLines = 1,
  meta,
  checked = false,
  onToggle,
  toggleLabel,
  onOpen,
  openLabel,
  openAccessoryLabel,
  onTitleClick,
  titleClickLabel,
  leading,
  trailing,
  actions,
  titleEditor,
  selected = false,
  disabled = false,
  pending = false,
  density,
  as: Element = "div",
  className,
}: ListRowProps) {
  const body = (
    <span className="flex min-w-0 flex-1 flex-col justify-center gap-1 py-2 text-left">
      <span
        className={cx(
          "text-body [overflow-wrap:anywhere]",
          // `title-clamp` rather than `truncate`: one line from `sm` up, two
          // below it. A 40px row is a desktop density rule, and applying it to
          // a 320px screen turned eight different tasks into eight rows of
          // "Routine item…". See the utility's note in `index.css`.
          titleLines === 1 ? "title-clamp" : "line-clamp-2",
          selected && "font-semibold",
          checked ? "text-muted line-through decoration-subtle" : "text-ink",
        )}
      >
        {title}
      </span>
      {meta ? (
        <span className="flex min-w-0 items-center gap-2 text-caption text-muted">{meta}</span>
      ) : null}
    </span>
  );

  // A row being renamed *is* the selected row: it draws the rail either way, so
  // the one open editor is as findable as the one open peek.
  const marked = selected || titleEditor !== undefined;

  return (
    <Element
      data-density={density}
      data-selected={selected || undefined}
      className={cx(
        "group/row relative flex row-height items-stretch gap-2 px-2 sm:px-pad-tight",
        // Selection is instantaneous; hover may fade. They are different
        // channels precisely so the fade cannot make two rows look selected.
        // The fill is `bg-fill-4` — the lightest iOS fill tier, an *alpha*, so
        // it composites correctly over the page, over a lane and over a
        // material. It is not the whole signal: the rail and the weight are.
        //
        // NO RADIUS. This row lives inside `ListSection`'s clipped group card
        // (`ui/list/GroupedList`), whose rule 3 is that corners belong to the
        // group: a 10px `rounded-control` wash inside a 12px `rounded-surface`
        // card leaves a sliver of card showing on the first and last row, and
        // mid-list it reads as a floating pill rather than a full-bleed row —
        // which is not what the square `hover:bg-hover` beside it does, nor
        // what `GroupedRow`'s own `interactive` skin does. `TaskRow`'s capture
        // flash already obeys this; so does this.
        marked
          ? "bg-fill-4 rail-selected"
          : "transition-colors dur-fast hover:bg-hover active:bg-active",
        pending && "opacity-60",
        className,
      )}
    >
      {leading ? (
        <span className="flex w-8 shrink-0 items-center justify-center self-stretch pointer-coarse:w-tap">
          {leading}
        </span>
      ) : null}

      {titleEditor ? (
        <>
          {/* The tick's footprint, so the text starts in exactly the column it
              was reading from a moment ago. */}
          {onToggle ? <span aria-hidden className="size-5 shrink-0 self-center" /> : null}
          <span className="flex min-w-0 flex-1 items-center gap-3 self-center py-1">
            {titleEditor}
          </span>
        </>
      ) : onToggle && onTitleClick ? (
        /* Split targets: the tick is its own precise control and the title is a
           separate button. The tick keeps a 44px touch target via its padding
           while staying a 20px circle visually. */
        <>
          <button
            type="button"
            role="checkbox"
            aria-checked={checked}
            aria-label={toggleLabel}
            disabled={disabled}
            onClick={() => onToggle(!checked)}
            className={cx(
              "-m-1 flex shrink-0 items-center justify-center self-center rounded-control p-1",
              "pointer-coarse:min-h-tap pointer-coarse:min-w-tap",
              "disabled:pointer-events-none disabled:opacity-50",
            )}
          >
            <Tick checked={checked} />
          </button>
          <button
            type="button"
            onClick={onTitleClick}
            disabled={disabled}
            aria-label={titleClickLabel}
            className={cx(
              "flex min-w-0 flex-1 items-center gap-3 self-stretch rounded-control text-left",
              "disabled:pointer-events-none disabled:opacity-50",
            )}
          >
            {body}
          </button>
        </>
      ) : onToggle ? (
        <button
          type="button"
          role="checkbox"
          aria-checked={checked}
          aria-label={toggleLabel}
          disabled={disabled}
          onClick={() => onToggle(!checked)}
          className={cx(
            "flex min-w-0 flex-1 items-center gap-3 self-stretch rounded-control text-left",
            "disabled:pointer-events-none disabled:opacity-50",
          )}
        >
          <Tick checked={checked} />
          {body}
        </button>
      ) : onOpen ? (
        <Button
          variant="ghost"
          size="fit"
          fullWidth
          align="start"
          onClick={onOpen}
          disabled={disabled}
          aria-label={openLabel}
          className={cx(
            "flex min-w-0 flex-1 items-center gap-3 self-stretch rounded-control text-left",
            "disabled:pointer-events-none disabled:opacity-50",
          )}
        >
          {body}
        </Button>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-3 self-stretch">{body}</span>
      )}

      {actions && !titleEditor ? (
        <span className={cx("flex shrink-0 items-center gap-1 self-center", hoverReveal)}>
          {actions}
        </span>
      ) : null}

      {trailing && !titleEditor ? (
        <span className="flex shrink-0 items-center gap-2 self-center">{trailing}</span>
      ) : null}

      {onOpen && !titleEditor ? (
        // Disclosure is persistent row state, so it stays at the terminal edge
        // after transient hover actions and metadata. The row body may already
        // open the same detail; callers can give this control a distinct label.
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onOpen}
          disabled={disabled}
          aria-label={openAccessoryLabel ?? openLabel}
          title={openAccessoryLabel ?? openLabel}
          className="shrink-0 self-center"
        >
          <DisclosureChevron expanded={selected} />
        </Button>
      ) : null}
    </Element>
  );
}

/*
 * There was a `ListRowDivider` here — an exported primitive nothing imported,
 * next to two lists that each spelled the hairline their own way. The rule that
 * replaced it was `divide-y divide-line` on the list container: no element per
 * gap, impossible to forget between two rows, identical for the skeleton.
 *
 * That rule is now retired here too. The iOS pass moved this row into the
 * grouped inset list, whose separator starts at the *text* rather than at the
 * container edge and therefore cannot be expressed by `divide-y` at all: the
 * list container is a `ListSection`, and each row draws its own
 * `row-separator`, an inset `::after` hairline that removes itself on
 * `:last-child`. See `ui/list/`. Nothing in the app spells a row divider by
 * hand.
 */
